import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { NgxPaginationModule } from 'ngx-pagination';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { ICprInstructor, ICprCertifiedStaff } from '../../../shared/models/cprFirstAidAed';
import { CprFirstAidAedService } from '../../../shared/services/cprFirstAidAed.service';
import { CprInstructorFormComponent } from './cpr-instructor-form/cpr-instructor-form.component';
import { CprCertifiedFormComponent } from './cpr-certified-form/cpr-certified-form.component';

const INSTRUCTORS = 'CPR/First Aid/AED Instructor on Staff';
const CERTIFIED = 'Staff Certified to Perform CPR/First Aid/AED';

@Component({
  selector: 'cpr-first-aid-aed',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    NgxPaginationModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    CprInstructorFormComponent,
    CprCertifiedFormComponent
  ],
  templateUrl: './cpr-first-aid-aed.component.html',
  styleUrl: './cpr-first-aid-aed.component.css'
})
export class CprFirstAidAedComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  tabs = [INSTRUCTORS, CERTIFIED];
  selectedTab: string = INSTRUCTORS;

  instructorsList: ICprInstructor[] = [];
  certifiedList: ICprCertifiedStaff[] = [];

  showInstructorForm: boolean = false;
  instructorFormItem: ICprInstructor | null = null;
  showCertifiedForm: boolean = false;
  certifiedFormItem: ICprCertifiedStaff | null = null;

  showDeleteConfirmation: boolean = false;
  deleteItem: ICprInstructor | ICprCertifiedStaff | null = null;
  deleteKind: 'instructor' | 'certified' | null = null;

  sortOrder: boolean = true;
  instructorPage: number = 1;
  certifiedPage: number = 1;
  itemsPerPage: number = 10;

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string = '';

  constructor(
    private userDataService: UserDataService,
    private cprService: CprFirstAidAedService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadForTab();
  }

  get isInstructorsTab(): boolean { return this.selectedTab === INSTRUCTORS; }
  get isCertifiedTab(): boolean { return this.selectedTab === CERTIFIED; }

  selectTab(tab: string): void {
    this.selectedTab = tab;
    this.loadForTab();
  }

  loadForTab(): void {
    if (this.isInstructorsTab) this.loadInstructors();
    else this.loadCertified();
  }

  // --- Instructors ---
  loadInstructors(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.instructorPage = 1;
    this.cprService.getInstructors(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        this.instructorsList = response && response.data ? response.data : [];
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  sortInstructors(key: keyof ICprInstructor): void {
    this.sortOrder = !this.sortOrder;
    this.instructorsList.sort((a, b) => this.compare(a[key], b[key]));
  }

  openAddInstructor(): void { this.instructorFormItem = null; this.showInstructorForm = true; }
  openEditInstructor(item: ICprInstructor): void { this.instructorFormItem = item; this.showInstructorForm = true; }
  closeInstructorForm(): void { this.showInstructorForm = false; this.instructorFormItem = null; }
  handleInstructorSaved(message: { type: string, message: string }): void {
    this.closeInstructorForm();
    this.loadInstructors();
    this.messageModal = message.type;
    this.popupMessage = message.message;
  }

  // --- Certified ---
  loadCertified(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.certifiedPage = 1;
    this.cprService.getCertifiedStaff(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        this.certifiedList = response && response.data ? response.data : [];
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  sortCertified(key: keyof ICprCertifiedStaff): void {
    this.sortOrder = !this.sortOrder;
    this.certifiedList.sort((a, b) => this.compare(a[key], b[key]));
  }

  openAddCertified(): void { this.certifiedFormItem = null; this.showCertifiedForm = true; }
  openEditCertified(item: ICprCertifiedStaff): void { this.certifiedFormItem = item; this.showCertifiedForm = true; }
  closeCertifiedForm(): void { this.showCertifiedForm = false; this.certifiedFormItem = null; }
  handleCertifiedSaved(message: { type: string, message: string }): void {
    this.closeCertifiedForm();
    this.loadCertified();
    this.messageModal = message.type;
    this.popupMessage = message.message;
  }

  // --- Delete (shared) ---
  openDelete(item: ICprInstructor | ICprCertifiedStaff, kind: 'instructor' | 'certified'): void {
    this.deleteItem = item;
    this.deleteKind = kind;
    this.showDeleteConfirmation = true;
  }
  closeDelete(): void { this.showDeleteConfirmation = false; this.deleteItem = null; this.deleteKind = null; }
  handleDelete(item: ICprInstructor | ICprCertifiedStaff): void {
    this.showDeleteConfirmation = false;
    const kind = this.deleteKind;
    if (!this.currentBuildingId || !item?.id) { this.closeDelete(); return; }
    this.loading = true;
    const request$ = kind === 'certified'
      ? this.cprService.deleteCertifiedStaff(this.currentBuildingId, String(item.id))
      : this.cprService.deleteInstructor(this.currentBuildingId, String(item.id));

    request$.subscribe({
      next: () => {
        this.loading = false;
        this.messageModal = 'success';
        this.popupMessage = 'This occurred after deleting a record.';
        if (kind === 'certified') this.loadCertified(); else this.loadInstructors();
        this.deleteItem = null; this.deleteKind = null;
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
        this.deleteItem = null; this.deleteKind = null;
      }
    });
  }

  private compare(a: any, b: any): number {
    const valueA = a?.toString().toLowerCase() || '';
    const valueB = b?.toString().toLowerCase() || '';
    if (valueA < valueB) return this.sortOrder ? -1 : 1;
    if (valueA > valueB) return this.sortOrder ? 1 : -1;
    return 0;
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
